/**
 * Suggestion chips for the client Overview. The backend's generated chips
 * (`source: "llm"`) are written from the client's recent activity, which on a
 * client with an open case is mostly case work ("Review the cover letter",
 * "Check the I-485 forms"). Those belong in the case IDE, so the client
 * surface drops them and offers a curated list of things the assistant does
 * at the client focus: send or chase an intake, book an appointment, fill the
 * record from documents, find what is missing, set a follow-up, summarise.
 * The rule chips (overdue reminders, an intake to review, an expiring
 * passport) stay: they are real, client-level items.
 *
 * Each entry maps to client-focus tools: `build_intake` / `send_intake` /
 * `remind_intake`, `create_reminder` (REMINDER or APPOINTMENT),
 * `read_client_document` + `update_client_profile`, `list_client_documents`,
 * `get_client_profile`, `list_client_reminders`.
 */
import type { ClientDashboard, Recommendation } from "./types";

/** How many chips the row shows, rule chips included. */
export const CLIENT_CHIP_COUNT = 4;
/** The curated chips always get at least this many of those slots. */
const MIN_CURATED = 2;

const starter = (
  id: string,
  title: string,
  prompt: string,
  reason: string
): Recommendation => ({
  id: `client-starter:${id}`,
  source: "starter",
  title,
  reason,
  severity: "info",
  action: { type: "prompt", prompt }
});

/**
 * The curated client chips, in the order they are offered. The list adapts to
 * what the summary says: an intake already out becomes a reminder about it,
 * and filling the record from documents only shows once there are documents.
 */
export const clientSuggestionPool = (
  summary: ClientDashboard | null | undefined
): Recommendation[] => {
  const inFlight = summary?.intakes_in_flight ?? [];
  const documents = summary?.counts?.documents ?? 0;
  const pool: (Recommendation | null)[] = [
    inFlight.length
      ? starter(
          "remind-intake",
          "Remind the Client About the Intake",
          `This client still has ${
            inFlight[0]?.title ? `"${inFlight[0].title}"` : "an intake"
          } open. Say how far they got and send them a reminder.`,
          "An intake is with the client and hasn't come back."
        )
      : starter(
          "send-intake",
          "Send an Intake to the Client",
          "Send this client an intake that covers what is missing from their profile, and tell me which documents we should request next.",
          "Collect their details and documents by email."
        ),
    starter(
      "schedule-appointment",
      "Schedule an Appointment with the Client",
      "Schedule an appointment with this client. Ask me for the date, time and location if I haven't given them, then add it to their calendar.",
      "Adds the appointment to the client's calendar."
    ),
    documents > 0
      ? starter(
          "autofill-record",
          "Autofill the Client Record from Documents",
          "Read this client's uploaded documents and fill in any blank or outdated fields in their profile from them. Show me what you would change before saving.",
          "Uses the documents already in the client's file."
        )
      : null,
    starter(
      "missing-documents",
      "Check What Documents Are Still Missing",
      "Check which documents are still missing from this client's file for their open cases, and list what we should request from them.",
      "Compares the client's documents with what their cases need."
    ),
    starter(
      "follow-up-reminder",
      "Create a Reminder to Follow Up with the Client",
      "Create a reminder to follow up with this client. Ask me when it should be due if I haven't said.",
      "Adds a reminder to the client's calendar."
    ),
    starter(
      "summary",
      "Summarize the Client's Profile and Open Items",
      "Summarize this client's profile and everything still open for them: cases, reminders, intakes and expiring documents.",
      "A quick read on where this client stands."
    ),
    starter(
      "expirations",
      "Check What Expires in the Next 90 Days",
      "What expires in the next 90 days for this client or their family? Say what each one is and when.",
      "Passports, visas and work permits coming due."
    )
  ];
  return pool.filter((p): p is Recommendation => p !== null);
};

/**
 * The chips to show: the backend's rule chips first (never its generated
 * ones), then a window of the curated pool that fills the row. `round`
 * advances the window, so "New Suggestions" cycles the curated list.
 */
export const clientChips = (
  recommendations: readonly Recommendation[],
  summary: ClientDashboard | null | undefined,
  round: number
): Recommendation[] => {
  const rules = recommendations.filter((r) => r.source === "rule");
  const pool = clientSuggestionPool(summary);
  const slots = Math.min(
    pool.length,
    Math.max(MIN_CURATED, CLIENT_CHIP_COUNT - rules.length)
  );
  const start = pool.length ? (round * slots) % pool.length : 0;
  const curated = Array.from(
    { length: slots },
    (_, i) => pool[(start + i) % pool.length]
  );
  const seen = new Set(
    rules.map((r) =>
      r.action.type === "prompt" ? r.action.prompt.toLowerCase() : r.id
    )
  );
  return [
    ...rules,
    ...curated.filter(
      (c) =>
        c.action.type !== "prompt" || !seen.has(c.action.prompt.toLowerCase())
    )
  ];
};
